/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import '../../vendors/youtube-player';
import { AbstractVideo } from './abstract-video';
import { Player } from './player';

/**
 * Youtube model
 *
 * @typedef {import('./metadata').Metadata} Metadata
 */
export class Youtube extends AbstractVideo {
  static TYPE = 'youtube';
  static PARAMS = ['autoplay', 'cc_lang_pref', 'cc_load_policy', 'color', 'controls', 'disablekb', 'enablejsapi', 'end', 'fs', 'hl', 'iv_load_policy', 'list', 'listType', 'loop', 'mute', 'modestbranding', 'origin', 'playlist', 'playsinline', 'rel', 'start', 'widget_referrer'];
  static EVENTS = ['onReady', 'onStateChange', 'onPlaybackQualityChange', 'onPlaybackRateChange', 'onError', 'onApiChange'];
  static ID_PATTERN = /(?:^|\/|=|:)([^"&?/\s=#.:]{11})(?:\?|&|\/|"|,|$)/;
  static TYPE_PATTERN = /youtube|youtu\.be|y2u\.be|i\.ytimg\.|^[^"&?/\s=#.:]{11}(?:\?|,|$)/;

  /**
   * @abstract
   * @protected
   */
  get oembedUrl() {
    const videoUrl = `https://www.youtube.com/watch?v=${this.id}`;
    const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}`;

    return oembedUrl;
  }

  /**
   * Create new Youtube player instance for the given response metadata
   *
   * @protected
   *
   * @param {Metadata} metadata Youtube metadata object, see [Youtube: Working with oEmbed](https://developer.vimeo.com/api/oembed/videos#embedding-a-video-with-oembed-step-1)
   *
   * @returns {Player} Returns new Youtube player instance
   */
  async createPlayer(metadata) {
    return new Promise((resolve, reject) => {
      const uid = `video-${this.element.ambid}-${this.id}`;
      const div = document.createElement('div');

      div.setAttribute('id', uid);

      this.applyClip(div);
      this.element.appendChild(div);
      this.convertParams(this.options.params);

      /* global YT */
      const player = new YT.Player(uid, {
        videoId: this.id,
        playerVars: {
          ...this.options.params,

          // Always enable youtube js API
          enablejsapi: true,
          origin: window.location.origin,

          // Note: The `loop` parameter has limited support in iFrame embeds.
          //       To loop a single video, set the `loop` parameter value to `1` and set the `playlist` parameter value to the same video ID.
          // See: Player Parameters - https://developers.google.com/youtube/player_parameters#loop
          playlist: this.id
        },
        events: {
          onError: event => {
            reject(event);

            if (typeof this.options.events.onError === 'function') {
              this.options.events.onError(this, event);
            }
          },
          onReady: (event) => {
            this.player.isReady = true;

            resolve(this.player);

            if (typeof this.options.events.onReady === 'function') {
              this.options.events.onReady(this, event);
            }
          }
        }
      });

      this.player = new Player(player, this.constructor.TYPE, this.options.params);
      this.element.player = this.player;
    });
  }
}
